// Pure helpers for the milestone summary strip.
//
// summariseMilestones — single pass over the milestone list producing the
// totals, counts, "next" pointer, and "slipped" list the four MetricCards
// need. Locked semantics:
//
// - valueAccepted sums paymentAmount ?? 0 over every milestone where
//   acceptedAt is set. Governance ($0) milestones contribute 0 but are
//   NEVER skipped — both numerator and denominator include them. The
//   acceptedCount and milestoneCount are likewise total-counts.
// - "next" picks the earliest non-delivered milestone whose plannedDate
//   is on or after today. Strictly future or today only.
// - "slipped" includes milestones where deliveredAt > plannedDate (late
//   delivery) OR where deliveredAt is not set and plannedDate < today
//   (overdue). daysLate is computed against plannedDate in both cases.
//   "worst" is the entry with the largest daysLate.
//
// formatCurrencyAbbreviated — strict-decimal rendering of dollar amounts:
// 2-dp for M, 1-dp for K, 0-dp for raw. The `long` form is the full-
// precision display + the GST status suffix used in tooltips. The
// suffix is sourced from the contract's stored intent — see
// utils/gstSuffix for the three-state mapping (ex GST / no GST /
// status not recorded).

import type { Milestone } from "../../../../types";
import {
  deriveMilestoneLifecycleStatus,
  deriveLate,
} from "../../../../utils/deriveMilestoneStatus";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { formatAud2dp } from "../../../../utils/formatCurrency";

export interface SlippedEntry {
  milestone: Milestone;
  daysLate: number;
}

export interface MilestoneSummary {
  contractValueTotal: number;
  milestoneCount: number;
  valueAccepted: number;
  acceptedCount: number;
  /** Integer 0..100. Returns 0 when contractValueTotal is 0/undefined to
   *  avoid divide-by-zero NaN propagation. */
  valueAcceptedPct: number;
  next: { milestone: Milestone; daysUntil: number } | undefined;
  slipped: SlippedEntry[];
  worst: SlippedEntry | undefined;
}

function isoDayUTC(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoDayUTC(toIso) - isoDayUTC(fromIso)) / 86_400_000);
}

export function summariseMilestones(
  milestones: readonly Milestone[],
  contractValue: number | undefined,
  today: string,
): MilestoneSummary {
  const milestoneCount = milestones.length;
  let valueAccepted = 0;
  let acceptedCount = 0;
  const slipped: SlippedEntry[] = [];
  let next: { milestone: Milestone; daysUntil: number } | undefined;

  for (const m of milestones) {
    const status = deriveMilestoneLifecycleStatus(m);
    const isAccepted = m.acceptedAt != null && m.acceptedAt.length > 0;

    if (isAccepted) {
      valueAccepted += m.paymentAmount ?? 0;
      acceptedCount += 1;
    }

    if (deriveLate(m)) {
      // Delivered but late — measure days from plannedDate to deliveredAt.
      slipped.push({
        milestone: m,
        daysLate: daysBetween(m.plannedDate, m.deliveredAt!),
      });
    } else if (
      status === "upcoming" &&
      m.deliveredAt == null &&
      isoDayUTC(m.plannedDate) < isoDayUTC(today)
    ) {
      // Overdue: plannedDate has passed, not yet delivered.
      slipped.push({
        milestone: m,
        daysLate: daysBetween(m.plannedDate, today),
      });
    } else if (status === "upcoming" && m.deliveredAt == null) {
      // Candidate for "next" — non-overdue, not yet delivered.
      const daysUntil = daysBetween(today, m.plannedDate);
      if (
        daysUntil >= 0 &&
        (next === undefined || daysUntil < next.daysUntil)
      ) {
        next = { milestone: m, daysUntil };
      }
    }
  }

  const contractValueTotal = contractValue ?? 0;
  const valueAcceptedPct =
    contractValueTotal > 0
      ? Math.round((valueAccepted / contractValueTotal) * 100)
      : 0;

  let worst: SlippedEntry | undefined;
  for (const s of slipped) {
    if (worst === undefined || s.daysLate > worst.daysLate) worst = s;
  }

  return {
    contractValueTotal,
    milestoneCount,
    valueAccepted,
    acceptedCount,
    valueAcceptedPct,
    next,
    slipped,
    worst,
  };
}

export interface AbbreviatedCurrency {
  short: string;
  long: string;
}

/**
 * Strict-decimal abbreviated currency. Returns:
 *   $1.20M / $1,200,000.00 (ex GST)        // gstApplies=true
 *   $12.5K / $12,500.00 (no GST)            // gstApplies=false
 *   $999   / $999.00 (GST status not recorded)  // gstApplies=undefined
 *
 * Trailing zeros stay (e.g. $1.50M) — financial figures read precise
 * that way. Use the `long` form (with the GST suffix) wherever a
 * dollar amount is exposed to a procurement audience.
 */
export function formatCurrencyAbbreviated(
  value: number,
  gstApplies?: boolean,
): AbbreviatedCurrency {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  let short: string;
  if (abs >= 1_000_000) {
    short = `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  } else if (abs >= 1_000) {
    short = `${sign}$${(abs / 1_000).toFixed(1)}K`;
  } else {
    short = `${sign}$${Math.round(abs)}`;
  }
  const long = `${formatAud2dp(value)} (${gstSuffix(gstApplies)})`;
  return { short, long };
}
